// src/pages/dashboard/CardDetail.tsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { MastercardIcon } from '../../components/ui/MastercardIcon';
import { EyeIcon } from '../../components/ui/Icons';
import { getServerUrl } from '../../config/server.config';
import TopUpModal from '../../components/dashboard/mastercard/TopUpModal';
// URL de la imagen de fondo de la tarjeta
import mastercardBgImage from '../../assets/images/mastercard-bg.png';
import { TransactionHistory } from '../../components/dashboard/TransactionHistory';
import { useDispatch } from 'react-redux';
import { getTransactionHistory} from '../../store/transactionReducer'
import type { AppDispatch, RootState } from '../../store';
// Configuración de posicionamiento - Puedes ajustar estos valores para mover los elementos
const CARD_LAYOUT = {
  // Posición del CVV (ajusta estos valores para mover horizontalmente)
  cvvLeft: 80,        // Cuanto menor el número, más a la izquierda

  // Posición del STATUS (ajusta estos valores para mover horizontalmente)
  statusRight: 20,    // Cuanto mayor el número, más a la izquierda
  statusBottom: 20    // Posición vertical desde abajo
};

// Componente para la página de detalle de una tarjeta
export default function CardDetail() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
   const { data:transactionsHistory, status} = useSelector((state: RootState) => state.transaction.history);
  const [cardDetails, setCardDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [isActionInProgress, setIsActionInProgress] = useState<boolean>(false);
  const [showFullCardNumber, setShowFullCardNumber] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [historyOption, setHistoryOption] = useState<string>("default")
  const [quarter, setQuarter] = useState<number>(1)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)
 
  // Consulta para obtener los detalles de la tarjeta
  const { data: cardData, isLoading: loadingCard, refetch: refetchCardDetails } = useQuery({
    queryKey: ['card-details', cardId],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        // Obtener detalles de la tarjeta
        const response = await fetch(`${getServerUrl(true)}/providers/verestro/cards/${cardId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch card details: ${response.statusText}`);
        }

        const detailsData = await response.json();
        console.log("Card details response:", detailsData);

        // Obtener saldo de la tarjeta
        const balanceResponse = await fetch(`${getServerUrl(true)}/providers/verestro/cards/${cardId}/balance`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!balanceResponse.ok) {
          throw new Error(`Failed to fetch card balance: ${balanceResponse.statusText}`);
        }

        const balanceData = await balanceResponse.json();
        console.log("Card balance response:", balanceData);

        // Combinar ambos conjuntos de datos pero ELIMINAR explícitamente cualquier propiedad currency
        if (detailsData.success && detailsData.data && balanceData.success && balanceData.data) {
          const result = {
            ...detailsData.data,
            balance: balanceData.data.balance.toString(),
            // currency: balanceData.data.currency || 'EUR', // ELIMINADA
            cardNumber: detailsData.data.cardNo || '',
            status: balanceData.data.cardDetails?.status || 'ACTIVE'
          };

          // Asegurarse de que no hay propiedad currency
          if (result.currency) {
            delete result.currency;
          }

          return result;
        } else {
          throw new Error('No card data received');
        }
      } catch (error) {
        console.error('Error fetching card details:', error);
        throw error;
      }
    },
    staleTime: 60000, // 1 minuto
    refetchOnWindowFocus: true
  });

  // Consulta para obtener las transacciones de la tarjeta con paginación
  const { data: transactionsData, isLoading: loadingTransactions, refetch: refetchTransactions } = useQuery({
    queryKey: ['card-transactions', cardId, currentPage, pageSize],
    queryFn: async () => {
      try {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        // Determinar el tamaño de página según la opción
        let size = 10; // Default
        if (historyOption === 'more') {
          size = 50; // Cargar más transacciones
        } else if (historyOption === 'quarter') {
          size = 100; // Cargar todas para filtrar por cuarto
        }

        const response = await fetch(`${getServerUrl(true)}/providers/verestro/cards/${cardId}/transactions?page=${currentPage}&size=${size}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch transactions: ${response.statusText}`);
        }

        const data = await response.json();
        console.log("Card transactions response:", data);

        // Extrayendo las transacciones de la estructura de respuesta correcta
        // El backend devuelve data.data.transactions, NO data.data.content
        if (data.success && data.data && data.data.transactions) {
          return data.data.transactions;
        } else if (data.success && data.data && data.data.content) {
          // Fallback para compatibilidad antigua
          return data.data.content;
        } else {
          return [];
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
        return [];
      }
    },
    staleTime: 60000, // 1 minuto
    refetchOnWindowFocus: true
  });

  // Al inicializar el componente, verifiquemos y modifiquemos el objeto cardDetails
  useEffect(() => {
    if (cardData) {
      // Crear una copia modificada del objeto cardDetails sin la propiedad currency
      const modifiedCardDetails = { ...cardData };

      // Eliminar explícitamente la propiedad currency
      delete modifiedCardDetails.currency;

      // Actualizar el estado con los datos modificados
      setCardDetails(modifiedCardDetails);
    }
    setIsLoading(loadingCard);
  }, [cardData, loadingCard]);

  // Función para filtrar transacciones por cuarto
  const filterTransactionsByQuarter = (transactions: any[], quarterNum: number) => {
    const currentYear = new Date().getFullYear();
    const startMonth = (quarterNum - 1) * 3;
    const endMonth = startMonth + 3;

    return transactions.filter((tx: any) => {
      const txDate = new Date(tx.transactionDate || tx.date || tx.timestamp || tx.createdAt);
      const txYear = txDate.getFullYear();
      const txMonth = txDate.getMonth();

      return txYear === currentYear && txMonth >= startMonth && txMonth < endMonth;
    });
  };

  // Procesamiento y normalización de las transacciones
  useEffect(() => {
    if (transactionsData) {
      // Normalizar las transacciones de tarjeta para coincidir con el formato que espera la UI
      let normalizedTransactions = transactionsData.map((tx: any) => {
        // Para MasterCard, determinamos el tipo basado en el campo 'type'
        const isCardDeposit = tx.type === 'TOP_UP';

        // Usamos merchantName como descripción principal, igual que en la vista de Transactions
        let displayDescription = '';

        if (isCardDeposit) {
          displayDescription = 'Card Top-Up';
        } else {
          // Para compras, usamos el merchantName como se muestra en Transactions
          displayDescription = tx.merchantName || 'Purchase';
        }

        return {
          ...tx,
          displayDescription: displayDescription, // Campo nuevo para la visualización
          isIncoming: isCardDeposit,
          // Normalizar campos de fecha para que funcione con TransactionHistory
          timestamp: tx.transactionDate || tx.timestamp || tx.date || tx.createdAt,
          date: tx.transactionDate || tx.date || tx.timestamp || tx.createdAt,
        };
      });

      // Aplicar filtrado por cuarto si está seleccionado
      if (historyOption === 'quarter' && quarter) {
        normalizedTransactions = filterTransactionsByQuarter(normalizedTransactions, quarter);
      }

      setTransactions(normalizedTransactions);
    }
  }, [transactionsData, historyOption, quarter]);
  // Refetch transacciones cuando cambie la opción (para MasterCard)
  useEffect(() => {
    // Solo refetch si es una tarjeta MasterCard (no usar Redux)
    if (cardId && historyOption) {
      refetchTransactions();
    }
  }, [historyOption, cardId, refetchTransactions])
  // Función para bloquear una tarjeta
  const [showLockModal, setShowLockModal] = useState(false);
  const [selectedLockReason, setSelectedLockReason] = useState('');

  const lockCard = async () => {
    if (!cardId || isActionInProgress) return;

    // Mostrar el modal de selección de razón
    setShowLockModal(true);
  };

  const handleConfirmLock = async () => {
    if (!selectedLockReason) {
      toast.error('Please select a reason for blocking the card');
      return;
    }

    setShowLockModal(false);
    setSelectedLockReason('');

    try {
      setIsActionInProgress(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${getServerUrl(true)}/providers/verestro/cards/${cardId}/toggle-block`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'block',
          reason: selectedLockReason
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to lock card: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Lock card response:", data);

      if (data.success) {
        toast.success('Card locked successfully');
        // Actualizar el estado localmente primero
        if (cardDetails) {
          cardDetails.status = 'BLOCKED';
        }
        // Luego refrescar desde el servidor
        setTimeout(() => {
          refetchCardDetails();
        }, 500);
      } else {
        throw new Error(data.message || 'Failed to lock card');
      }
    } catch (error) {
      console.error('Error locking card:', error);
      toast.error('Failed to lock card. Please try again.');
    } finally {
      setIsActionInProgress(false);
    }
  };
const getCurrentQuarter=()=> {
  const currentMonth = new Date().getMonth(); // getMonth() returns 0-11
  return Math.floor(currentMonth / 3) + 1; // Calculate quarter (1-4)
}
  // Función para desbloquear una tarjeta
  const unlockCard = async () => {
    if (!cardId || isActionInProgress) return;

    try {
      setIsActionInProgress(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${getServerUrl(true)}/providers/verestro/cards/${cardId}/toggle-block`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'unblock'
          // No se necesita razón para desbloquear
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to unlock card: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Unlock card response:", data);

      if (data.success) {
        toast.success('Card unlocked successfully');
        // Actualizar el estado localmente primero
        if (cardDetails) {
          cardDetails.status = 'ACTIVE';
        }
        // Luego refrescar desde el servidor
        setTimeout(() => {
          refetchCardDetails();
        }, 500);
      } else {
        throw new Error(data.message || 'Failed to unlock card');
      }
    } catch (error) {
      console.error('Error unlocking card:', error);
      toast.error('Failed to unlock card. Please try again.');
    } finally {
      setIsActionInProgress(false);
    }
  };

  // Función para regenerar el CVV de una tarjeta
  const regenerateCvv = async () => {
    if (!cardId || isActionInProgress) return;

    try {
      setIsActionInProgress(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${getServerUrl(true)}/providers/verestro/cards/${cardId}/cvv/regenerate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to regenerate CVV: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Regenerate CVV response:", data);

      if (data.success && data.data) {
        toast.success('CVV regenerated successfully');
        // Actualizar los datos de la tarjeta con el nuevo CVV
        setCardDetails(prev => ({
          ...prev,
          cvv: data.data.cvv.toString()
        }));
      } else {
        throw new Error(data.message || 'Failed to regenerate CVV');
      }
    } catch (error) {
      console.error('Error regenerating CVV:', error);
      toast.error('Failed to regenerate CVV. Please try again.');
    } finally {
      setIsActionInProgress(false);
    }
  };

  // Función para cambiar el PIN de la tarjeta
  const changePin = async (newPin: string) => {
    if (!cardId || isActionInProgress || !newPin) return;

    try {
      setIsActionInProgress(true);
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${getServerUrl(true)}/providers/verestro/cards/${cardId}/pin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          newPin: newPin // El backend espera 'newPin', no 'pin'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to change PIN: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Change PIN response:", data);

      if (data.success) {
        toast.success('PIN changed successfully');
      } else {
        throw new Error(data.message || 'Failed to change PIN');
      }
    } catch (error) {
      console.error('Error changing PIN:', error);
      toast.error('Failed to change PIN. Please try again.');
    } finally {
      setIsActionInProgress(false);
    }
  };
  const handleTopUpModal = () => {

  }
  
  // Renderizar cargador si los datos están cargando
  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h1>Loading card details...</h1>
      </div>
    );
  }

  // Renderizar mensaje de error si no hay datos de tarjeta
  if (!cardDetails) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h1>Card not found</h1>
        <p>The card you are looking for does not exist or you do not have access to it.</p>
        <button
          onClick={() => navigate('/dashboard/wallets')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#B3A369',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
          Back to Wallets
        </button>
      </div>
    );
  }

  // Formatear el número de tarjeta para mostrar
  const formatCardNumber = (cardNumber: string, showFull: boolean) => {
    if (!cardNumber) return 'XXXX XXXX XXXX XXXX';

    // Si el número ya está enmascarado (con asteriscos o X), mostrarlo tal cual si no se pide el número completo
    if ((cardNumber.includes('*') || cardNumber.includes('X')) && !showFull) {
      return cardNumber;
    }

    // Formateamos el número de tarjeta para que se muestre en bloques de 4 dígitos
    const formattedNumber = cardNumber.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();

    // Si se pide mostrar el número completo, devolverlo
    if (showFull) {
      return formattedNumber;
    }

    // Mostrar solo los últimos 4 dígitos y enmascarar el resto
    const lastFourDigits = formattedNumber.slice(-4);
    return `XXXX XXXX XXXX ${lastFourDigits}`;
  };

  // Formatear fecha de expiración
  const formatExpiryDate = (date: string) => {
    if (!date) return '00/00';

    try {
      const expiryDate = new Date(date);
      const month = String(expiryDate.getMonth() + 1).padStart(2, '0');
      const year = String(expiryDate.getFullYear()).slice(2);
      return `${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return date;
    }
  };

  // Manejar la solicitud de cambio de PIN
  const handlePinChange = () => {
    // Mostrar un prompt para ingresar el nuevo PIN
    const newPin = prompt('Enter a new 4-digit PIN for your card:');

    if (newPin) {
      // Validar que el PIN tenga 4 dígitos
      if (/^\d{4}$/.test(newPin)) {
        changePin(newPin);
      } else {
        toast.error('PIN must be exactly 4 digits');
      }
    }
  };

  // Obtener el símbolo de moneda según el código
  const getCurrencySymbol = (currencyCode: string): string => {
    switch (currencyCode) {
      case 'EUR':
        return '€';
      case 'USD':
        return '$';
      case 'GBP':
        return '£';
      default:
        return currencyCode;
    }
  };
 
  return (
    <div style={{ padding: '40px' }}>
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            padding: '10px 15px',
            backgroundColor: '#f1f1f1',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
        >
          ← Back to Dashboard
        </button>
      </div>

      <h1 style={{ fontSize: '32px', marginBottom: '30px' }}>MasterCard Details</h1>

      {/* Card Display - Versión actualizada con imagen de fondo */}
      <div style={{
        width: '100%',
        maxWidth: '400px',
        height: '240px',
        borderRadius: '12px',
        color: 'white',
        marginBottom: '30px',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)'
      }}>
        {/* Fondo de la tarjeta */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${mastercardBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 1
        }}></div>

        {/* Contenido de la tarjeta (encima del fondo) */}
        <div style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
              {cardDetails.currency || 'EUR'}
            </div>
          </div>

          <div style={{
            marginTop: '20px',
            fontSize: '20px',
            letterSpacing: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>{formatCardNumber(cardDetails.cardNumber || cardDetails.cardNo || cardDetails.lastFourDigits || '', showFullCardNumber)}</div>

            {/* Botón para mostrar/ocultar número completo */}
            <button
              onClick={() => setShowFullCardNumber(!showFullCardNumber)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                padding: '8px'
              }}
              title={showFullCardNumber ? "Hide card number" : "Show full card number"}
            >
              {/* Usamos solo EyeIcon con diferentes estilos según el estado */}
              <EyeIcon
                size={24}
                color="white"
                style={{
                  opacity: showFullCardNumber ? 0.6 : 1,
                  textDecoration: showFullCardNumber ? 'line-through' : 'none'
                }}
              />
            </button>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto' }}>
            <div>
              <div style={{ fontSize: '12px', opacity: 0.7 }}>VALID THRU</div>
              <div>{formatExpiryDate(cardDetails.exp || cardDetails.expiryDate)}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', opacity: 0.7 }}>CVV</div>
              <div>{cardDetails.cvv || '***'}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', opacity: 0.7 }}>STATUS</div>
              <div style={{
                color: cardDetails.status === 'ACTIVE' ? '#4CAF50' : '#F44336',
                textShadow: '1px 1px 2px rgba(0,0,0,0.7)' // Agregar sombra para mejor legibilidad
              }}>
                {cardDetails.status || 'UNKNOWN'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Card Actions */}
      <div style={{
        display: 'flex',
        gap: '15px',
        marginBottom: '40px',
        flexWrap: 'wrap'
      }}>
        {/* Botón de bloqueo/desbloqueo según el estado */}
        {cardDetails.status === 'ACTIVE' ? (
          <button
            onClick={lockCard}
            style={{
              padding: '10px 20px',
              backgroundColor: '#F44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              opacity: isActionInProgress ? 0.7 : 1
            }}
            disabled={isActionInProgress}
          >
            {isActionInProgress ? 'Processing...' : 'Lock Card'}
          </button>
        ) : (
          <button
            onClick={unlockCard}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              opacity: isActionInProgress ? 0.7 : 1
            }}
            disabled={isActionInProgress}
          >
            {isActionInProgress ? 'Processing...' : 'Unlock Card'}
          </button>
        )}

        {/* Botón para regenerar CVV */}
        <button
          onClick={regenerateCvv}
          style={{
            padding: '10px 20px',
            backgroundColor: '#B3A369',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: isActionInProgress ? 0.7 : 1
          }}
          disabled={isActionInProgress}
        >
          {isActionInProgress ? 'Processing...' : 'Regenerate CVV'}
        </button>

        {/* Botón para cambiar PIN */}
        <button
          onClick={handlePinChange}
          style={{
            padding: '10px 20px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: isActionInProgress ? 0.7 : 1
          }}
          disabled={isActionInProgress}
        >
          {isActionInProgress ? 'Processing...' : 'Change PIN'}
        </button>
        <button
          onClick={() => { setIsModalOpen(true) }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#2ecf60',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: isActionInProgress ? 0.7 : 1
          }}
          disabled={isActionInProgress}
        >
          {isActionInProgress ? 'Processing...' : 'Top Up Card'}
        </button>
      </div>

      {/* Card Balance */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '10px' }}>Balance</h2>
        <div style={{
          fontSize: '36px',
          fontWeight: 'bold',
          color: '#B3A369'
        }}>
          {parseFloat(cardDetails.balance || '0').toLocaleString()} €
        </div>
      </div>

      {/* Transactions Section */}
      <div>
        <div className='flex flex-wrap justify-between items-center mb-4 '>
          <h2 style={{ fontSize: '24px' }}>Recent Transactions</h2>
          <select
            value={historyOption}
            onChange={(e) => setHistoryOption(e.target.value)}
            className="w-[11rem] text-sm !py-1"
          >
            <option value="default" >Default View</option>
            <option value="more" >More Transactions</option>
            <option value="quarter" >Quarter View</option>
          </select>
        </div>
        {historyOption==="quarter" && (

        <div className='flex flex-wrap gap-2'>
          <button
            onClick={() => { setQuarter(1) }}
            className={`py-2.5 px-5 ${quarter===1?"bg-green-400":"bg-belobaba-gold"} hover:!bg-background-gold-transparent hover:!text-black text-white border-none rounded `}
            disabled={1> getCurrentQuarter()}
          >
            1 Quarter
          </button>
          <button
            onClick={() => { setQuarter(2) }}
            className={`py-2.5 px-5 ${quarter===2?"bg-green-400":"bg-belobaba-gold"} hover:!bg-background-gold-transparent hover:!text-black text-white border-none rounded `}
            disabled={2> getCurrentQuarter()}
          >
            2 Quarter
          </button>
          <button
            onClick={() => { setQuarter(3) }}
            className={`py-2.5 px-5 ${quarter===3?"bg-green-400":"bg-belobaba-gold"} hover:!bg-background-gold-transparent hover:!text-black text-white border-none rounded `}
            disabled={3> getCurrentQuarter()}
          >
            3 Quarter
          </button>
          <button
            onClick={() => { setQuarter(4) }}
            className={`py-2.5 px-5 ${quarter===4?"bg-green-400":"bg-belobaba-gold"} hover:!bg-background-gold-transparent hover:!text-black text-white border-none rounded `}
            disabled={4> getCurrentQuarter()}
          >
            4 Quarter
          </button>
        </div>
        )}
        {status ==="loading" ? (
          <p>Loading transactions...</p>
        ) : transactions.length === 0 ? (
          <p>No transactions found for this card.</p>
        ) : (
          <TransactionHistory transactions={transactions} />
          // <div style={{ overflowX: 'auto' }}>
          //   <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          //     <thead>
          //       <tr>
          //         <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #ddd' }}>Date</th>
          //         <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #ddd' }}>Description</th>
          //         <th style={{ textAlign: 'right', padding: '10px', borderBottom: '1px solid #ddd' }}>Amount</th>
          //         <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #ddd' }}>Status</th>
          //       </tr>
          //     </thead>
          //     <tbody>
          //       {transactions.map((transaction) => {
          //         // Determinar si es una transacción de ingreso o gasto
          //         const isIncoming = transaction.isIncoming;

          //         return (
          //           <tr key={transaction.id || transaction.clientTransactionId || transaction.transactionId}>
          //             <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>
          //               {new Date(transaction.timestamp || transaction.date || transaction.createdAt).toLocaleDateString()}
          //             </td>
          //             <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>
          //               {/* Ahora usamos displayDescription que contiene el nombre del comerciante */}
          //               {transaction.displayDescription}
          //             </td>
          //             <td style={{ 
          //               padding: '10px', 
          //               borderBottom: '1px solid #ddd', 
          //               textAlign: 'right',
          //               color: isIncoming ? '#4CAF50' : '#F44336'
          //             }}>
          //               {isIncoming ? '+' : '-'} {Math.abs(parseFloat(transaction.amount)).toLocaleString()} €
          //             </td>
          //             <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>
          //               <span style={{
          //                 display: 'inline-block',
          //                 backgroundColor: transaction.status === 'PENDING' ? '#fff3cd' : '#d1e7dd',
          //                 color: transaction.status === 'PENDING' ? '#664d03' : '#0f5132',
          //                 padding: '5px 10px',
          //                 borderRadius: '4px',
          //                 fontSize: '12px'
          //               }}>
          //                 {transaction.status || 'COMPLETED'}
          //               </span>
          //             </td>
          //           </tr>
          //         );
          //       })}
          //     </tbody>
          //   </table>
          // </div>
        )}
      </div>
      {isModalOpen && (
        <TopUpModal
          isOpen={isModalOpen}
          onClose={(value) => setIsModalOpen(value)}
          cardId={cardDetails?.id || cardId}
        />
      )}

      {/* Modal para seleccionar razón de bloqueo */}
      {showLockModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#2c2416',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '450px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ color: '#d4af37', marginBottom: '20px' }}>
              Why do you want to block this card?
            </h3>

            <div style={{ marginBottom: '20px' }}>
              {[
                { value: 'CARD_LOST', label: '🔍 Card Lost', desc: 'I cannot find my card' },
                { value: 'CARD_STOLEN', label: '🚨 Card Stolen', desc: 'Someone stole my card' },
                { value: 'SUSPECTED_FRAUD', label: '⚠️ Suspected Fraud', desc: 'I see suspicious activity' },
                { value: 'CARD_INACTIVE', label: '⏸️ Temporarily Inactive', desc: 'I want to pause my card' },
                { value: 'PIN_TRIES_EXCEEDED', label: '🔐 PIN Issues', desc: 'Too many wrong PIN attempts' },
                { value: 'PENDING_QUERY', label: '❓ Pending Query', desc: 'I have questions about charges' },
                { value: 'CARD_CONSOLIDATION', label: '🔄 Card Consolidation', desc: 'Managing multiple cards' },
                { value: 'CARD_REPLACED', label: '🆕 Card Replaced', desc: 'I have a new card' }
              ].map((reason) => (
                <div
                  key={reason.value}
                  onClick={() => setSelectedLockReason(reason.value)}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    borderRadius: '8px',
                    backgroundColor: selectedLockReason === reason.value ? '#d4af37' : '#3d3427',
                    color: selectedLockReason === reason.value ? '#2c2416' : '#e8dcc4',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: `2px solid ${selectedLockReason === reason.value ? '#d4af37' : 'transparent'}`
                  }}
                  onMouseEnter={(e) => {
                    if (selectedLockReason !== reason.value) {
                      e.currentTarget.style.backgroundColor = '#4d3e31';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedLockReason !== reason.value) {
                      e.currentTarget.style.backgroundColor = '#3d3427';
                    }
                  }}
                >
                  <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{reason.label}</div>
                  <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>{reason.desc}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowLockModal(false);
                  setSelectedLockReason('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3d3427',
                  color: '#e8dcc4',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmLock}
                disabled={!selectedLockReason}
                style={{
                  padding: '10px 20px',
                  backgroundColor: selectedLockReason ? '#F44336' : '#666',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: selectedLockReason ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Block Card
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}